import clsx from 'clsx'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { BaseRatePackageView } from 'selectors/offerPage/offerPageSelectors'
import { areRatesLoading, getBaseRatePackagesView, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import AlwaysOnPriceDetails from './AlwaysOnPriceDetails'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import TextButton from 'LuxKit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'LuxKit/Typography/BodyText'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'LuxKit/Typography/Caption'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import RenderInclusions from './RenderInclusions'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTileHeaderArea from 'components/AccommodationKit/AccommodationPackageTileHeaderArea'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock, { ACCOMMODATION_PACKAGE_TITLE } from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MIN_LEAD_TIME_DAYS, FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import usePackageStatusCreditsCalculation from 'loyalty/hooks/usePackageStatusCreditsCalculation'
import useHotelTierProgressionEligibility from 'loyalty/hooks/useHotelTierProgressionEligibility'
import LoyaltyTierProgressionBanner from 'loyalty/components/Banners/LoyaltyTierProgressionBanner'
import { useAppSelector } from 'hooks/reduxHooks'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'

interface Props {
  optionNumber: number
  offer: App.Offer
  packageView: App.OfferPackageOptionView
  partnership: App.Partnership
  checkIn?: string
  duration: number
  rooms: Array<App.Occupants>
  calendarsLoading?: boolean
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  previewMode: boolean
  currency: string
  baseRatePackagesView: Array<BaseRatePackageView>
  defaultDepositAmountPercentage: number
  canViewLuxPlusBenefits: boolean
  isFlightsSelected?: boolean
  showOptionLabel?: boolean
  availableForPurchase?: boolean
}

function AlwaysOnRateOption(props: Props) {
  const {
    offer,
    packageView,
    partnership,
    checkIn,
    duration,
    rooms,
    optionNumber,
    calendarsLoading,
    flightOrigin,
    flightDestination,
    previewMode,
    currency,
    baseRatePackagesView,
    defaultDepositAmountPercentage,
    canViewLuxPlusBenefits,
    isFlightsSelected,
    showOptionLabel = true,
    availableForPurchase,
  } = props

  const {
    package: pkg,
    flightPrice,
    bundledDiscountPercent,
    hotelDiscountPercent,
  } = packageView

  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const checkOut = useMemo(() => checkOutMoment?.format(ISO_DATE_FORMAT), [checkOutMoment])

  const offerAvailableRates = useOfferAvailableRates(offer.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialOfferPageRooms,
  })

  const pkgAvailableRate = useMemo(() => offerAvailableRates.rates.find(rate => rate.packageUniqueKey === pkg.uniqueKey), [pkg, offerAvailableRates])
  const fetching = !!calendarsLoading || !!offerAvailableRates?.fetching
  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = pkg?.ignoreDynamicCancellationPolicy ?? false
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled

  const dynamicHotelAvailable = ((useDynamicCancellationPolicies && !!pkgAvailableRate && checkIn && !fetching) || !useDynamicCancellationPolicies || !checkIn || fetching)
  const hotelAvailable = packageView.hotelAvailable && dynamicHotelAvailable
  const canPurchase = availableForPurchase && packageView.capacityValid && hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && canPurchase && !fetching && packageView.fromCalendarInfo
  const isValidRoomOccupancy = packageView.flightsAvailable || offer.bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && !(hotelAvailable && !packageView.capacityValid && availableForPurchase && !canPurchase) && isValidRoomOccupancy
  const canAddToUserCart = canBuyNow

  const departureTooEarly = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInMoment])

  const refundable = !useDynamicCancellationPolicies && pkg.roomRate?.cancellationPolicy?.type !== 'non-refundable'

  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS, [checkInMoment])
  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooEarly

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent
  const shouldShowDiscount = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && (canPurchase || previewMode) && !pkg.roomRate?.inclusionsHideValue

  const baseRatePackageView = useMemo(() => {
    return baseRatePackagesView.find(
      (packageItem) => packageItem.duration === pkg.duration && packageItem.id === pkg.id,
    )
  }, [baseRatePackagesView, pkg.duration, pkg.id])

  const baseRateValue = canViewLuxPlusBenefits ? baseRatePackageView?.memberValue : baseRatePackageView?.value
  const inclusionsAmount = (canViewLuxPlusBenefits ? pkg.totalInclusionsAmount : pkg.inclusionsAmount) ?? 0
  const pkgValueOrPkgMemberValue = canViewLuxPlusBenefits ? pkg.memberValue : pkg.value
  const packageViewHotelValueOrMemberHotelValue = (canViewLuxPlusBenefits ? packageView.hotelMemberValue : packageView.hotelValue) ?? 0

  // We use base rate cost to calculate new totalValue and discountPercent, using the isBaseRate flag on rate plan level.
  // This shows a bigger discount
  const valuedUpTo = useMemo(() => {
    if (baseRateValue && (baseRateValue + inclusionsAmount) > pkgValueOrPkgMemberValue) {
      const displayedHotelValue = baseRateValue + inclusionsAmount + (packageView.propertyFees ?? 0)
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, packageView.hotelPrice)

      if (displayedHotelDiscountPercent > highestDiscountPercent) {
        return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
      }
    }

    return { value: packageViewHotelValueOrMemberHotelValue, percent: highestDiscountPercent }
  }, [baseRateValue, inclusionsAmount, pkgValueOrPkgMemberValue, packageViewHotelValueOrMemberHotelValue, packageView.propertyFees, packageView.hotelPrice, highestDiscountPercent])

  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, packageView.hotelTotal, packageView.package, pkgAvailableRate)

  const { inclusions, bonusInclusions, tacticalInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit = useMemo(() => {
    return isTasteOfGoldActive && isTasteOfGoldEligibleOffer(
      offer,
      currency,
      offer.property?.geoData?.countryCode?.toLowerCase() !== 'au',
      pkg.price,
    )
  }, [isTasteOfGoldActive, offer, currency, pkg.price])

  const showPartnership = !!packageView.hotelAvailable && !!partnership?.hasEarn

  const { travelFrom, travelTo } = useMemo(() => {
    const tacticalSchedule = pkg?.roomRate?.schedules?.find(x => x.type === 'tactical')
    let travelFrom = tacticalSchedule?.travelPeriod?.from
    let travelTo = tacticalSchedule?.travelPeriod?.to
    if ((!travelFrom || !travelTo) && tacticalInclusions.length > 0) {
      const tacticalInclusionsSchedule = tacticalInclusions[0].schedules?.find(x => x.type === 'tactical')
      travelFrom = tacticalInclusionsSchedule?.travelPeriod?.from
      travelTo = tacticalInclusionsSchedule?.travelPeriod?.to
    }

    return { travelFrom, travelTo }
  }, [pkg?.roomRate?.schedules, tacticalInclusions])

  const stayPayRateTier = useMemo(() => {
    return pkg?.stayPay?.find(tier => tier.stayNights === duration)
  }, [pkg?.stayPay, duration])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const isMobileDevice = useIsMobileDevice()
  const showHotelAndFlightsToggle = !isMobileDevice && flightsEnabled && !offer.bundledWithFlightsOnly
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected
  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: pkg.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !hideHotelPriceBlock,
    }),
  ], [hideHotelPriceBlock, offer, packageView, currentRegionCode, pkg.duration])

  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const { statusCredits: packageStatusCredits } = usePackageStatusCreditsCalculation({
    offer,
    packageView,
    nights: duration,
    skip: !hasLoyaltyAccount,
  })

  const tierProgressionEligibility = useHotelTierProgressionEligibility({
    statusCredits: packageStatusCredits,
    nights: duration,
    checkoutDate: checkOutMoment?.format('YYYY-MM-DD'),
    skip: !hasLoyaltyAccount,
  })

  const { showTierProgressionBanner, highestUnlockableTier, highestUnlockableTierName } = tierProgressionEligibility

  const travelBetweenText = travelFrom && travelTo ? intl.formatMessage(
    {
      id: 'zXCDa3bChyl_EL_a-Wq2',
      defaultMessage: 'Travel between: {travelFromDate} - {travelToDate}.',
      description: 'Travel date range for tactical rate option on the offer detail page',
    },
    {
      travelFromDate: moment(travelFrom).format(DMY_CASUAL_SHORT_FORMAT),
      travelToDate: moment(travelTo).subtract(pkg.duration, 'days').format(DMY_CASUAL_SHORT_FORMAT),
    },
  ) : undefined

  const minimumStayText = intl.formatMessage(
    {
      id: 'RCust_O8OioxQbj_CfW4',
      defaultMessage: 'Minimum stay: {count, plural, one {# night} other {# nights}}',
      description: 'Minimum night stay requirement for rate option on the form',
    },
    { count: pkg.duration },
  )

  const qualifyFreeNightsText = stayPayRateTier ? intl.formatMessage(
    {
      id: 'Sx55e7RcCyHPDjPmSZ8u',
      defaultMessage: 'Your travel dates qualify for {count, plural, one {# free night} other {# free nights}}',
      description: 'Message showing stay-pay promotion qualifying free nights',
    },
    { count: stayPayRateTier.freeNights },
  ) : undefined

  const viewMoreInclusionsText = intl.formatMessage({
    id: 'jtt2ZyDb5es7UV8zBvtd',
    defaultMessage: 'View more inclusions',
    description: 'Toggle text to expand inclusions list for rate option',
  })

  const viewLessInclusionsText = intl.formatMessage({
    id: 'Fwi75EXcnb2Dl4R4MQPM',
    defaultMessage: 'View less inclusions',
    description: 'Toggle text to collapse inclusions list for rate option',
  })

  const signUpLabel = intl.formatMessage({
    id: 'LmmQqwMETUdi6PIWkYAe',
    defaultMessage: 'Sign up to view exclusive rates',
    description: 'Button prompting guest to sign up to see exclusive hotel rates',
  })

  const bookNowLabel = intl.formatMessage({
    id: 'r_S0deLwOj0dfkVHBjob',
    defaultMessage: 'Book now',
    description: 'Button text to book now on always-on rate option on the form',
  })

  const maxGuestsExceededLabel = intl.formatMessage({
    id: '9hjqeQt_9yRfe2ldV7ll',
    defaultMessage: 'Max number of guests exceeded',
    description: 'Caption shown when room capacity is exceeded on the room page',
  })

  const addAnotherLabel = intl.formatMessage(
    {
      id: 'R1UYevB5y_kLUo-kGrzW',
      defaultMessage: 'Add another {saleUnit}',
      description: 'Button to add another room/unit when capacity is exceeded',
    },
    { saleUnit: offer.saleUnit },
  )

  const soldOutLabel = intl.formatMessage({
    id: '_5hxBsQSKSCpPJB3gwDk',
    defaultMessage: 'Sold out for your selected dates.',
    description: 'Caption shown when hotel is sold out for selected dates',
  })

  const changeAirportLabel = intl.formatMessage({
    id: 'rOtG2duLg7uI6Eg_fa9f',
    defaultMessage: 'Change airport',
    description: 'Button to change departing airport when no flights available',
  })

  const changeDepartureDateLabel = intl.formatMessage({
    id: 'VOoVVjcVVHExvY0Yc-7p',
    defaultMessage: 'Change departure date',
    description: 'Button to change departure date when too close to check-in',
  })

  const noFlightsFromAirportText = intl.formatMessage(
    {
      id: 'KCe0399qOgmK-1GZSQxq',
      defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
      description: 'Error message when no flights are available from the selected airport',
    },
    { airportName: flightOrigin?.name ?? '' },
  )

  const addFlightsText = intl.formatMessage({
    id: 'CZxhYhJIU2InbWTVRXvr',
    defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
    description: 'Info text explaining flights can be added to hotel package',
  })

  const flightsTooFarText = intl.formatMessage({
    id: 'nW-s6s_wrzcn0c2oITdD',
    defaultMessage: 'Flights can only be booked up to 11 months in advance.',
    description: 'Warning that flights cannot be booked more than 11 months ahead',
  })

  const flightsTooSoonText = intl.formatMessage({
    id: 'DxLT1cCSXIxMKmXcJkG1',
    defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours',
    description: 'Warning that flights are unavailable when check-in is less than 24 hours away',
  })

  return (
    <AccommodationPackageTile data-testid={pkg.uniqueKey}>
      {showTierProgressionBanner && (
        <AccommodationPackageTileHeaderArea>
          <LoyaltyTierProgressionBanner
            highestUnlockableTier={highestUnlockableTier}
            highestUnlockableTierName={highestUnlockableTierName}
            checkoutDate={checkOut}
          />
        </AccommodationPackageTileHeaderArea>
      )}
      <AccommodationPackageTileContentArea>
        <AccommodationPackageTileDetails
          packageNumber={showOptionLabel ? optionNumber : undefined}
          roomPackage={packageView.package}
          tagLineLabel={packageView.package.hasTactical && isLPCTacticalEnabled() && <ProductTypeLabel productType="limited_time_special" />}
          stayPayRateTier={stayPayRateTier}
        >
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              <TAOCancellationPolicies
                offerId={offer.id}
                useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
                timezoneOffset={offer.property?.timezoneOffset}
                uniqueKey={pkg.uniqueKey}
                checkInDate={checkIn}
                checkOutDate={checkOut}
                occupants={rooms}
                cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
                offerType={offer.type}
              />
              {!!showPartnership && pkg.partnership &&
                <OfferPackagePartnershipLabel
                  offer={offer}
                  price={packageView.hotelPrice}
                  packagePartnership={pkg.partnership}
                />}
              {packageView.package.hasTactical && isLPCTacticalEnabled() && <BodyText variant="small" weight="normal" colour="neutral-two">
                {[travelBetweenText, minimumStayText].filter(Boolean).join(' ')}
              </BodyText>}

              {stayPayRateTier && qualifyFreeNightsText && <BodyText variant="small" colour="neutral-one">
                {qualifyFreeNightsText}
              </BodyText>}
            </Group>
          </Group>
        </AccommodationPackageTileDetails>
        <div>
          {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
          <LargeContentToggle
            maxHeight={208}
            showMoreText={viewMoreInclusionsText}
            showLessText={viewLessInclusionsText}
          >
            <RenderInclusions
              bonusInclusions={bonusInclusions}
              inclusions={inclusions}
              luxPlusInclusions={luxPlusInclusions}
              offer={offer}
              stayPayRateTier={stayPayRateTier}
            />
          </LargeContentToggle>
        </div>
      </AccommodationPackageTileContentArea>
      <AccommodationPackageTilePricingArea>
        <WalledContent
          enforced={offer.walledGarden}
          wall={(
            <Group fullHeight direction="vertical" verticalAlign="end">
              <TextButton size="large" kind="secondary" onClick={onSignUpClick}>{signUpLabel}</TextButton>
            </Group>
          )}
        >
          <Group direction="horizontal" gap={4} wrap="wrap">
            {shouldShowDiscount && <PriceRowValueDiscount
              type="estimate"
              discountPercentage={valuedUpTo.percent}
              data-testid="always-on-rate-option-discount"
            />}
            {isDepositEnabled && <SecureWithDepositTagAndModal
              pkgAvailableRate={pkgAvailableRate}
              offerType={offer.type}
              depositAmountPercentage={depositAmountPercentage}
              depositThresholds={offer.depositThresholds}
            />}
          </Group>
          <AccommodationPackageTilePricingBlocks>
            {!offer.bundledWithFlightsOnly && !hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
              data-testid="pricing"
              title={(flightsEnabled && !showHotelAndFlightsToggle) ? ACCOMMODATION_PACKAGE_TITLE.HOTEL : undefined}
              callToAction={<Group direction="vertical" gap={8}>
                {canBuyNow && <OfferBookButton
                  size="large"
                  type="submit"
                  disabled={fetching}
                  data-testid={clsx('HotelBookingBtn', 'buy-now-button', { refundable })}
                  packageView={packageView}
                >
                  {bookNowLabel}
                </OfferBookButton>}
                {canAddToUserCart && <OfferAddToCartButton
                  size="large"
                  type="submit"
                  disabled={fetching}
                  packageView={packageView}
                />}
                {availableForPurchase && !canPurchase && <>
                  {hotelAvailable && !packageView.capacityValid && <div>
                    <Caption variant="large" align="center">{maxGuestsExceededLabel}</Caption>
                    {!isFlightForceBundled(offer) && <TextButton
                      size="large"
                      kind="tertiary"
                      onClick={onToggleFilter}
                      data-filter="capacity"
                    >
                      {addAnotherLabel}
                    </TextButton>}
                  </div>}
                  {!hotelAvailable && <Caption variant="large">{soldOutLabel}</Caption>}
                </>}
              </Group>}
            >
              {(canPurchase || previewMode) && <VerticalSpacer gap={12}>
                <div>
                  <Caption variant="medium">
                    <AlwaysOnPriceDetails
                      rooms={rooms}
                      checkIn={checkIn}
                      packageView={packageView}
                      offer={offer}
                      canBuyNow={canBuyNow}
                      valuedUpTo={valuedUpTo}
                    />
                    <LoadingIndicator visible={fetching} floating opaque />
                  </Caption>
                </div>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={!checkIn ? 'estimate' : 'exact'}
                />
              </VerticalSpacer>}
            </AccommodationPackageTilePricingBlock>}
            {flightsEnabled && hotelAvailable && !hideHotelAndFlightsPriceBlock && <AccommodationPackageTilePricingBlock
              data-testid="pricing"
              title={(!offer.bundledWithFlightsOnly && !showHotelAndFlightsToggle) ? ACCOMMODATION_PACKAGE_TITLE.HOTEL_AND_FLIGHTS : undefined}
              callToAction={<Group direction="vertical" gap={8}>
                {!canAddToUserCart && canBuyNow && flightsBookable && (
                  <OfferBookButton
                    size="large"
                    disabled={fetching}
                    data-testid="HotelAndFlightsBtn"
                    data-buytype="flights"
                    packageView={packageView}
                  >
                    {bookNowLabel}
                  </OfferBookButton>
                )}
                {canAddToUserCart && flightsBookable && <OfferSelectPackageButton
                  size="large"
                  disabled={fetching}
                  data-buytype="flights"
                  packageView={packageView}
                />}
                {availableForPurchase && !canPurchase && !hotelAvailable && <BodyText variant="medium">
                  {soldOutLabel}
                </BodyText>}
                {!hasFlights && flightOrigin.code === flightDestination?.code && <TextButton
                  size="large"
                  data-filter="airport"
                  kind="tertiary"
                  onClick={onToggleFilter}
                >
                  {changeAirportLabel}
                </TextButton>}
                {flightOrigin.code !== flightDestination?.code && departureTooEarly && <TextButton
                  size="large"
                  data-filter="checkin"
                  kind="tertiary"
                  onClick={onToggleFilter}
                >
                  {changeDepartureDateLabel}
                </TextButton>}
              </Group>}
            >
              <VerticalSpacer gap={12}>
                <div>
                  <VerticalSpacer gap={12}>
                    {!hasFlights && flightOrigin.code === flightDestination?.code && <BodyText
                      variant="small"
                      colour="neutral-two"
                    >
                      {noFlightsFromAirportText}
                    </BodyText>}
                    {!hasFlights && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture && !departureTooEarly && (
                      <BodyText variant="small" colour="neutral-two">
                        {addFlightsText}
                      </BodyText>
                    )}
                    {!hasFlights && departureTooFarInFuture && (
                      <BodyText variant="small" colour="neutral-two">
                        {flightsTooFarText}
                      </BodyText>
                    )}
                    {flightOrigin.code !== flightDestination?.code && departureTooEarly && <BodyText
                      variant="small"
                      colour="neutral-two"
                    >
                      {flightsTooSoonText}
                    </BodyText>}
                  </VerticalSpacer>
                  {((hasFlights && !departureTooEarly) || (hasFlights && previewMode)) && (
                    <Caption variant="medium" data-testid="pricing">
                      <AlwaysOnPriceDetails
                        rooms={rooms}
                        checkIn={checkIn}
                        packageView={packageView}
                        offer={offer}
                        canBuyNow={canBuyNow}
                        isFlightPrice
                        valuedUpTo={valuedUpTo}
                      />
                      <LoadingIndicator visible={fetching} floating opaque />
                    </Caption>
                  )}
                  <AtolProtectedTooltip />
                </div>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType="estimate"
                />
              </VerticalSpacer>
            </AccommodationPackageTilePricingBlock>}
          </AccommodationPackageTilePricingBlocks>
          <PriceRowAgentHubCommission
            offerId={offer.id}
            productType={offer.productType}
            offerTotal={packageView.hotelPrice}
            packageId={pkg.id}
          />
        </WalledContent>
      </AccommodationPackageTilePricingArea>
    </AccommodationPackageTile>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  calendarsLoading: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey] || areRatesLoading(appState, state),
  previewMode: appState.system.previewMode,
  flightOrigin: state.airport,
  currency: appState.geo.currentCurrency,
  baseRatePackagesView: getBaseRatePackagesView(appState, state),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isFlightsSelected: state.flights,
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), AlwaysOnRateOption, preventRenderWithOpenFilter)
